// Product Gallery JavaScript
export function initProductGallery() {
  class ProductGallery {
    constructor() {
      this.gallery = document.querySelector('product-gallery');
      this.mediaList = this.gallery?.querySelector('.product-gallery__media-list');
      this.thumbnails = this.gallery?.querySelectorAll('.product-gallery__thumbnail');
      this.zoomButtons = this.gallery?.querySelectorAll('[is="product-zoom-button"]');
      
      this.currentIndex = parseInt(this.gallery?.dataset.initialIndex || 0);
      this.isMobile = window.innerWidth < 1000;
      this.isInitialized = false;
      
      this.init();
    }

    init() {
      if (!this.gallery || this.isInitialized) return;
      
      // Initialize Intersection Observer for lazy loading
      this.initLazyLoading();
      
      // Initialize event listeners
      this.initEventListeners();
      
      // Initialize zoom functionality if enabled
      if (this.zoomButtons.length > 0) {
        this.initZoom();
      }
      
      this.isInitialized = true;
    }

    initLazyLoading() {
      const options = {
        root: null,
        rootMargin: '50px',
        threshold: 0.1
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const media = entry.target;
            const img = media.querySelector('img');
            
            if (img && img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            
            observer.unobserve(media);
          }
        });
      }, options);

      // Observe all media items except the first one
      this.gallery.querySelectorAll('.product-gallery__media:not(:first-child)').forEach(media => {
        observer.observe(media);
      });
    }

    initEventListeners() {
      // Handle thumbnail clicks
      this.thumbnails?.forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => this.goToSlide(index));
      });

      // Handle touch events for mobile
      if (this.isMobile) {
        this.initTouchEvents();
      }

      // Handle keyboard navigation
      document.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.goToSlide(this.currentIndex - 1);
        } else if (e.key === 'ArrowRight') {
          this.goToSlide(this.currentIndex + 1);
        }
      });

      // Handle window resize
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.isMobile = window.innerWidth < 1000;
          this.updateLayout();
        }, 100);
      });
    }

    initTouchEvents() {
      let startX, startY;
      const threshold = 50;

      this.mediaList?.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      this.mediaList?.addEventListener('touchend', (e) => {
        if (!startX || !startY) return;

        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;
        
        const deltaX = endX - startX;
        const deltaY = endY - startY;

        // Only handle horizontal swipes
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > threshold) {
          if (deltaX > 0) {
            this.goToSlide(this.currentIndex - 1);
          } else {
            this.goToSlide(this.currentIndex + 1);
          }
        }

        startX = null;
        startY = null;
      }, { passive: true });
    }

    initZoom() {
      this.zoomButtons.forEach(button => {
        button.addEventListener('click', () => {
          const media = button.closest('.product-gallery__media');
          const img = media?.querySelector('img');
          
          if (!img) return;

          if (media.classList.contains('zoomed')) {
            this.exitZoom(media);
          } else {
            this.enterZoom(media);
          }
        });
      });
    }

    enterZoom(media) {
      const img = media.querySelector('img');
      if (!img) return;

      media.classList.add('zoomed');
      img.style.transform = 'scale(2)';
      img.style.cursor = 'zoom-out';
    }

    exitZoom(media) {
      const img = media.querySelector('img');
      if (!img) return;

      media.classList.remove('zoomed');
      img.style.transform = 'scale(1)';
      img.style.cursor = 'zoom-in';
    }

    goToSlide(index) {
      const slides = this.gallery.querySelectorAll('.product-gallery__media:not([hidden])');
      if (!slides.length) return;

      // Handle wrap-around
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;

      this.currentIndex = index;
      
      // Update active states
      slides.forEach((slide, i) => {
        slide.classList.toggle('active', i === index);
      });

      this.thumbnails?.forEach((thumb, i) => {
        thumb.setAttribute('aria-current', i === index);
      });

      // Scroll to the selected slide using scrollTo instead of scrollIntoView
      // (scrollIntoView with smooth behavior blocks JS execution on iOS 15-16)
      if (this.mediaList) {
        const slide = slides[index];
        const containerRect = this.mediaList.getBoundingClientRect();
        const slideRect = slide.getBoundingClientRect();
        const scrollLeft = this.mediaList.scrollLeft + (slideRect.left - containerRect.left) - (containerRect.width / 2 - slideRect.width / 2);
        this.mediaList.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }

    updateLayout() {
      // Update layout based on screen size
      this.gallery.classList.toggle('mobile', this.isMobile);
      
      // Update thumbnail visibility
      if (this.thumbnails) {
        this.thumbnails.forEach(thumb => {
          thumb.style.display = this.isMobile ? 'none' : '';
        });
      }
    }
  }

  // Initialize the gallery
  return new ProductGallery();
} 